/**
 * TOSS Web Application - Roles Constants
 *
 * Role definitions, groupings, and helper functions for the TOSS system.
 * Based on PROJECT_DOCUMENTATION.md Section 2 (10 roles).
 */

import { UserRole, UserRoleLabels } from '@/types';

// ============================================================================
// Role Groups
// ============================================================================

/**
 * Roles that can self-register via mobile app
 * These roles can register themselves without admin intervention
 */
export const SELF_REGISTER_ROLES: readonly UserRole[] = [
  UserRole.Mechanic,
  UserRole.Intern,
] as const;

/**
 * Roles that can only be created by Administrator or SuperAdmin
 */
export const ADMIN_CREATE_ONLY_ROLES: readonly UserRole[] = [
  UserRole.SuperAdmin,
  UserRole.Administrator,
  UserRole.Accountant,
  UserRole.Chef,
  UserRole.HeadOfFinancialAndMaterialProvision,
  UserRole.HeadOfDispatchingService,
  UserRole.HeadOfPTO,
  UserRole.Dispatcher,
] as const;

/**
 * Roles that work primarily in mobile app.
 *
 * HeadOfPTO moved here 2026-09-05: the role becomes a field role (a mechanic who also sees and
 * assigns work across their PTO group), and the web-office half of its old job — services and
 * PTO directories — passes to HeadOfDispatchingService.
 */
export const MOBILE_PRIMARY_ROLES: readonly UserRole[] = [
  UserRole.Mechanic,
  UserRole.Intern,
  UserRole.HeadOfPTO,
] as const;

/**
 * Roles that work primarily in web interface
 */
export const WEB_PRIMARY_ROLES: readonly UserRole[] = [
  UserRole.SuperAdmin,
  UserRole.Administrator,
  UserRole.Accountant,
  UserRole.Chef,
  UserRole.HeadOfFinancialAndMaterialProvision,
  UserRole.HeadOfDispatchingService,
  UserRole.Dispatcher,
] as const;

/**
 * Management roles (heads and executives)
 */
export const MANAGEMENT_ROLES: readonly UserRole[] = [
  UserRole.SuperAdmin,
  UserRole.Administrator,
  UserRole.Chef,
  UserRole.HeadOfFinancialAndMaterialProvision,
  UserRole.HeadOfDispatchingService,
  UserRole.HeadOfPTO,
] as const;

// ============================================================================
// Role Metadata
// ============================================================================

/**
 * Role metadata interface
 */
export interface RoleMetadata {
  /** Role enum value */
  role: UserRole;
  /** Display label in Russian */
  label: string;
  /** Short code for the role */
  code: string;
  /** Brief description of the role */
  description: string;
  /** Whether role can self-register */
  canSelfRegister: boolean;
  /** Primary interface (mobile/web) */
  primaryInterface: 'mobile' | 'web';
  /** Whether this is a management role */
  isManagement: boolean;
  /**
   * `'internal'` (staff — LK/mobile) or `'external'` (the client portal audience).
   * `ROLE_METADATA` being `Record<UserRole, RoleMetadata>` is what makes this field
   * compile-forced: a new `UserRole` member fails the build here until classified,
   * mirroring the backend's `UserAudience` split (phase0-part1). `INTERNAL_ROLES`
   * below derives from this — never add a role to a staff dropdown by hand.
   */
  audience: 'internal' | 'external';
}

/**
 * Complete metadata for all roles
 */
export const ROLE_METADATA: Record<UserRole, RoleMetadata> = {
  [UserRole.SuperAdmin]: {
    role: UserRole.SuperAdmin,
    label: UserRoleLabels[UserRole.SuperAdmin],
    code: 'SUA',
    description: 'Полный системный доступ, управление организациями',
    canSelfRegister: false,
    primaryInterface: 'web',
    isManagement: true,
    audience: 'internal',
  },
  [UserRole.Mechanic]: {
    role: UserRole.Mechanic,
    label: UserRoleLabels[UserRole.Mechanic],
    code: 'MCH',
    description: 'Выполнение работ, осмотр ПТО, отчёты, свои расходы',
    canSelfRegister: true,
    primaryInterface: 'mobile',
    isManagement: false,
    audience: 'internal',
  },
  [UserRole.Intern]: {
    role: UserRole.Intern,
    label: UserRoleLabels[UserRole.Intern],
    code: 'INT',
    description: 'То же, что механик (ограниченные права)',
    canSelfRegister: true,
    primaryInterface: 'mobile',
    isManagement: false,
    audience: 'internal',
  },
  [UserRole.Administrator]: {
    role: UserRole.Administrator,
    label: UserRoleLabels[UserRole.Administrator],
    code: 'ADM',
    description: 'Полный доступ ко всем модулям',
    canSelfRegister: false,
    primaryInterface: 'web',
    isManagement: true,
    audience: 'internal',
  },
  [UserRole.Accountant]: {
    role: UserRole.Accountant,
    label: UserRoleLabels[UserRole.Accountant],
    code: 'ACC',
    description: 'Проверка расходов, выставление счетов, ПТО',
    canSelfRegister: false,
    primaryInterface: 'web',
    isManagement: false,
    audience: 'internal',
  },
  [UserRole.Chef]: {
    role: UserRole.Chef,
    label: UserRoleLabels[UserRole.Chef],
    code: 'CHF',
    description: 'Полный доступ ко всем модулям',
    canSelfRegister: false,
    primaryInterface: 'web',
    isManagement: true,
    audience: 'internal',
  },
  [UserRole.HeadOfFinancialAndMaterialProvision]: {
    role: UserRole.HeadOfFinancialAndMaterialProvision,
    label: UserRoleLabels[UserRole.HeadOfFinancialAndMaterialProvision],
    code: 'FMO',
    description: 'Проверка ПТО, управление оплатой, материалы',
    canSelfRegister: false,
    primaryInterface: 'web',
    isManagement: true,
    audience: 'internal',
  },
  [UserRole.HeadOfDispatchingService]: {
    role: UserRole.HeadOfDispatchingService,
    label: UserRoleLabels[UserRole.HeadOfDispatchingService],
    code: 'HDS',
    description: 'Управление заявками, назначение механиков',
    canSelfRegister: false,
    primaryInterface: 'web',
    isManagement: true,
    audience: 'internal',
  },
  [UserRole.HeadOfPTO]: {
    role: UserRole.HeadOfPTO,
    label: UserRoleLabels[UserRole.HeadOfPTO],
    code: 'HPT',
    // 2026-09-05: moved from the web office to the mobile app — see MOBILE_PRIMARY_ROLES.
    description: 'Бригадир: осмотр, назначение механиков в своей группе',
    canSelfRegister: false,
    primaryInterface: 'mobile',
    isManagement: true,
    audience: 'internal',
  },
  [UserRole.Dispatcher]: {
    role: UserRole.Dispatcher,
    label: UserRoleLabels[UserRole.Dispatcher],
    code: 'DSP',
    description: 'Создание заявок, клиенты, направления',
    canSelfRegister: false,
    primaryInterface: 'web',
    isManagement: false,
    audience: 'internal',
  },
  [UserRole.Client]: {
    role: UserRole.Client,
    label: UserRoleLabels[UserRole.Client],
    code: 'CLI',
    description: 'Представитель компании-заказчика. Внешний клиентский портал (/api/client/*) — вне матрицы прав, своя авторизационная политика ClientAccess.',
    canSelfRegister: false,
    primaryInterface: 'web',
    isManagement: false,
    audience: 'external',
  },
} as const;

// ============================================================================
// Role Selection Options
// ============================================================================

/**
 * Role option for select/dropdown components
 */
export interface RoleOption {
  value: UserRole;
  label: string;
}

/**
 * Every role classified `audience: 'internal'` in ROLE_METADATA above — i.e. every
 * role except the external client portal. This is the list every staff-facing role
 * dropdown (worker create/edit, role filters, the permissions matrix) must derive
 * from, directly or indirectly: `UserRole`/`UserRoleLabels` alone say nothing about
 * which roles belong on an internal staff-management screen, and a role added there
 * without checking this list is exactly how `Client` would end up selectable as a
 * worker's role.
 */
export const INTERNAL_ROLES: readonly UserRole[] = Object.values(UserRole).filter(
  (role) => ROLE_METADATA[role].audience === 'internal'
);

/**
 * All internal roles as options for select components. Named "ALL" for the roles a
 * staff dropdown should ever show — deliberately excludes the external client role,
 * see INTERNAL_ROLES.
 */
export const ALL_ROLE_OPTIONS: RoleOption[] = INTERNAL_ROLES.map((role) => ({
  value: role,
  label: UserRoleLabels[role],
}));

/**
 * Self-registrable roles as options
 */
export const SELF_REGISTER_ROLE_OPTIONS: RoleOption[] = SELF_REGISTER_ROLES.map((role) => ({
  value: role,
  label: UserRoleLabels[role],
}));

/**
 * Admin-creatable roles as options
 */
export const ADMIN_CREATE_ROLE_OPTIONS: RoleOption[] = INTERNAL_ROLES.map((role) => ({
  value: role,
  label: UserRoleLabels[role],
}));

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a role can self-register
 */
export function canSelfRegister(role: UserRole): boolean {
  return SELF_REGISTER_ROLES.includes(role);
}

/**
 * Check if a role is a management role
 */
export function isManagementRole(role: UserRole): boolean {
  return MANAGEMENT_ROLES.includes(role);
}

/**
 * Check if a role primarily uses mobile interface
 */
export function isMobilePrimaryRole(role: UserRole): boolean {
  return MOBILE_PRIMARY_ROLES.includes(role);
}

/**
 * Check if a role primarily uses web interface
 */
export function isWebPrimaryRole(role: UserRole): boolean {
  return WEB_PRIMARY_ROLES.includes(role);
}

/**
 * Get role metadata
 */
export function getRoleMetadata(role: UserRole): RoleMetadata {
  return ROLE_METADATA[role];
}

/**
 * Get role label (Russian)
 */
export function getRoleLabel(role: UserRole): string {
  return UserRoleLabels[role];
}

/**
 * Get role by code
 */
export function getRoleByCode(code: string): UserRole | undefined {
  const entry = Object.entries(ROLE_METADATA).find(([, metadata]) => metadata.code === code);
  return entry ? (entry[0] as UserRole) : undefined;
}
